/**
 * Presentation-only ordering for admin timelines. The API remains responsible
 * for pagination and every business classification; these helpers merely make
 * a fully-loaded display collection deterministic.
 */
export type TemporalValue = string | null | undefined;
export type SourceIdentity = string | number | null | undefined;

function isMissing(value: TemporalValue): boolean {
  return value == null || value.trim() === '';
}

/** Descending temporal comparison with absent values always at the end. */
export function compareTemporalDescending(left: TemporalValue, right: TemporalValue): number {
  const leftMissing = isMissing(left);
  const rightMissing = isMissing(right);
  if (leftMissing || rightMissing) {
    if (leftMissing && rightMissing) return 0;
    return leftMissing ? 1 : -1;
  }
  return right!.localeCompare(left!);
}

/** A deterministic descending source-identity tie-breaker. */
export function compareIdentityDescending(left: SourceIdentity, right: SourceIdentity): number {
  if (left == null || right == null) {
    if (left == null && right == null) return 0;
    return left == null ? 1 : -1;
  }
  if (typeof left === 'number' && typeof right === 'number') return right - left;
  return String(right).localeCompare(String(left));
}

/**
 * Compare date plus local start time and then a stable source identity. ISO
 * timestamps and YYYY-MM-DD/HH:MM API values are deliberately comparable as
 * strings, preserving their wire chronology without parsing in the browser.
 */
export function compareDateTimeDescending(
  left: { date: TemporalValue; time?: TemporalValue; id: SourceIdentity },
  right: { date: TemporalValue; time?: TemporalValue; id: SourceIdentity },
): number {
  return compareTemporalDescending(left.date, right.date)
    || compareTemporalDescending(left.time, right.time)
    || compareIdentityDescending(left.id, right.id);
}

export function compareDateDescending(
  left: { date: TemporalValue; id: SourceIdentity },
  right: { date: TemporalValue; id: SourceIdentity },
): number {
  return compareTemporalDescending(left.date, right.date)
    || compareIdentityDescending(left.id, right.id);
}

export function orderByDateTimeDescending<T>(
  items: readonly T[],
  key: (item: T) => { date: TemporalValue; time?: TemporalValue; id: SourceIdentity },
): T[] {
  return [...items].sort((left, right) => compareDateTimeDescending(key(left), key(right)));
}

export function orderByDateDescending<T>(
  items: readonly T[],
  key: (item: T) => { date: TemporalValue; id: SourceIdentity },
): T[] {
  return [...items].sort((left, right) => compareDateDescending(key(left), key(right)));
}
